import { estimateMemoryUsage } from './surveyImport.js';

/**
 * Preview generation utilities for external survey imports
 * Creates UI-friendly preview data structures for display before import
 */

/**
 * Generate comprehensive preview data for UI display
 * @param {Object} surveyData - Transformed survey data
 * @param {Object} options - Preview options
 * @returns {Object} Preview data structure optimized for UI components
 */
export function generateImportPreview(surveyData, options = {}) {
  const {
    maxPreviewRows = 10,
    maxQuestionPreview = 5,
    includeMemoryEstimate = true,
    includeDataQuality = true
  } = options;

  const { importRecord, responses, summary } = surveyData;
  const questionMapping = JSON.parse(importRecord['Question Mapping']);

  // Basic import information
  const importInfo = {
    soNumber: importRecord.SO,
    surveyTitle: importRecord['Survey Title'],
    sourceType: importRecord['Source Type'],
    originalFilename: importRecord['Original Filename'],
    importDate: importRecord['Import Date'],
    importedBy: importRecord['Imported By'],
    notes: importRecord['Import Notes']
  };

  // Questions overview
  const questionsOverview = {
    total: questionMapping.questions.length,
    byType: questionMapping.questions.reduce((acc, q) => {
      acc[q.type] = (acc[q.type] || 0) + 1;
      return acc;
    }, {}),
    preview: questionMapping.questions.slice(0, maxQuestionPreview).map(q => ({
      id: q.id,
      title: q.title.length > 80 ? q.title.substring(0, 80) + '...' : q.title,
      type: q.type,
      column: q.column,
      required: q.required || false,
      hasOptions: !!(q.options && q.options.length > 0),
      hasScale: !!(q.scale),
      typeIcon: getQuestionTypeIcon(q.type),
      typeLabel: getQuestionTypeLabel(q.type)
    })),
    hasMore: questionMapping.questions.length > maxQuestionPreview
  };

  // Response data overview
  const responsesOverview = {
    total: responses.length,
    preview: responses.slice(0, maxPreviewRows).map((response, index) => {
      const responsePreview = {
        rowNumber: index + 1,
        originalRow: response.metadata.original_row,
        dataQuality: response.metadata.data_quality,
        qualityIcon: getDataQualityIcon(response.metadata.data_quality),
        qualityColor: getDataQualityColor(response.metadata.data_quality),
        validationWarnings: response.metadata.validation_warnings || [],
        answers: {}
      };

      // Sample key answers for preview
      const keyQuestions = questionMapping.questions.slice(0, 3);
      keyQuestions.forEach(question => {
        const answer = response.responses[question.id];
        if (answer) {
          responsePreview.answers[question.id] = {
            question: question.title,
            answer: formatAnswerForPreview(answer.answer, question.type),
            type: question.type,
            rawValue: answer.raw_value
          };
        }
      });

      return responsePreview;
    }),
    hasMore: responses.length > maxPreviewRows
  };

  // Data quality analysis
  let dataQualityAnalysis = null;
  if (includeDataQuality) {
    dataQualityAnalysis = generateDataQualityAnalysis(responses, questionMapping.questions);
  }

  // Memory usage estimate
  let memoryEstimate = null;
  if (includeMemoryEstimate) {
    memoryEstimate = estimateMemoryUsage(surveyData);
  }

  // Summary statistics
  const statisticalSummary = {
    responses: {
      total: summary.total_responses,
      complete: summary.complete_responses,
      partial: summary.partial_responses,
      invalid: summary.invalid_responses,
      completionRate: ((summary.complete_responses / summary.total_responses) * 100).toFixed(1)
    },
    questions: {
      total: summary.questions_count,
      byType: questionsOverview.byType
    },
    estimatedImportTime: estimateImportTime(responses.length),
    airtableRecordsToCreate: {
      importRecords: 1,
      responseRecords: responses.length,
      totalRecords: responses.length + 1
    }
  };

  // Validation summary
  const validationSummary = generateValidationSummary(responses, questionMapping.questions);

  // Recommendations
  const recommendations = generateImportRecommendations(surveyData, dataQualityAnalysis);

  return {
    importInfo,
    questionsOverview,
    responsesOverview,
    dataQualityAnalysis,
    statisticalSummary,
    validationSummary,
    recommendations,
    memoryEstimate,
    metadata: {
      previewGeneratedAt: new Date().toISOString(),
      previewOptions: options,
      version: '1.0'
    }
  };
}

/**
 * Generate data quality analysis
 * @param {Array} responses - Response data
 * @param {Array} questions - Question definitions
 * @returns {Object} Data quality analysis
 */
function generateDataQualityAnalysis(responses, questions) {
  const analysis = {
    overall: {
      score: 0,
      grade: 'F',
      description: ''
    },
    byQuestion: {},
    patterns: {
      commonIssues: [],
      strengths: [],
      concerns: []
    }
  };

  // Calculate overall quality score
  let totalQualityPoints = 0;
  let maxPossiblePoints = 0;

  responses.forEach(response => {
    switch (response.metadata.data_quality) {
      case 'complete':
        totalQualityPoints += 10;
        break;
      case 'partial':
        totalQualityPoints += 6;
        break;
      case 'invalid':
        totalQualityPoints += 0;
        break;
    }
    maxPossiblePoints += 10;
  });

  const overallScore = maxPossiblePoints > 0 ? (totalQualityPoints / maxPossiblePoints) * 100 : 0;
  analysis.overall.score = Math.round(overallScore);

  // Assign grade
  if (overallScore >= 90) analysis.overall.grade = 'A';
  else if (overallScore >= 80) analysis.overall.grade = 'B';
  else if (overallScore >= 70) analysis.overall.grade = 'C';
  else if (overallScore >= 60) analysis.overall.grade = 'D';
  else analysis.overall.grade = 'F';

  // Generate description
  if (overallScore >= 90) {
    analysis.overall.description = 'Excellent data quality. Ready for import.';
  } else if (overallScore >= 70) {
    analysis.overall.description = 'Good data quality with minor issues.';
  } else if (overallScore >= 50) {
    analysis.overall.description = 'Acceptable data quality. Review warnings.';
  } else {
    analysis.overall.description = 'Poor data quality. Consider data source review.';
  }

  // Analyze by question
  questions.forEach(question => {
    const questionAnalysis = {
      completionRate: 0,
      validResponses: 0,
      invalidResponses: 0,
      commonValues: [],
      issues: []
    };

    let validCount = 0;
    let invalidCount = 0;
    const valueFrequency = {};

    responses.forEach(response => {
      const answer = response.responses[question.id];
      if (answer) {
        if (answer.answer !== null && answer.answer !== '') {
          validCount++;
          
          // Track value frequency for pattern analysis
          const valueKey = String(answer.answer).toLowerCase();
          valueFrequency[valueKey] = (valueFrequency[valueKey] || 0) + 1;
        } else {
          invalidCount++;
        }
      } else {
        invalidCount++;
      }
    });

    questionAnalysis.completionRate = ((validCount / responses.length) * 100).toFixed(1);
    questionAnalysis.validResponses = validCount;
    questionAnalysis.invalidResponses = invalidCount;

    // Find common values
    const sortedValues = Object.entries(valueFrequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
    
    questionAnalysis.commonValues = sortedValues.map(([value, count]) => ({
      value,
      count,
      percentage: ((count / validCount) * 100).toFixed(1)
    }));

    // Identify issues
    if (questionAnalysis.completionRate < 50) {
      questionAnalysis.issues.push('Low completion rate');
    }
    if (questionAnalysis.invalidResponses > questionAnalysis.validResponses) {
      questionAnalysis.issues.push('More invalid than valid responses');
    }

    analysis.byQuestion[question.id] = questionAnalysis;
  });

  // Identify patterns
  const lowCompletionQuestions = Object.entries(analysis.byQuestion)
    .filter(([, q]) => parseFloat(q.completionRate) < 30)
    .length;

  if (lowCompletionQuestions > 0) {
    analysis.patterns.concerns.push(`${lowCompletionQuestions} question(s) with low completion rates`);
  }

  const highCompletionQuestions = Object.entries(analysis.byQuestion)
    .filter(([, q]) => parseFloat(q.completionRate) > 90)
    .length;

  if (highCompletionQuestions > questions.length * 0.7) {
    analysis.patterns.strengths.push('Most questions have high completion rates');
  }

  return analysis;
}

/**
 * Generate validation summary for preview
 * @param {Array} responses - Response data
 * @param {Array} questions - Question definitions
 * @returns {Object} Validation summary
 */
function generateValidationSummary(responses, questions) {
  const summary = {
    totalIssues: 0,
    errorCount: 0,
    warningCount: 0,
    topIssues: [],
    readinessCheck: {
      isReady: true,
      blockers: [],
      recommendations: []
    }
  };

  const issueFrequency = {};

  // Collect all validation warnings
  responses.forEach(response => {
    const warnings = response.metadata.validation_warnings || [];
    warnings.forEach(warning => {
      issueFrequency[warning] = (issueFrequency[warning] || 0) + 1;
      summary.warningCount++;
    });
  });

  summary.totalIssues = summary.errorCount + summary.warningCount;

  // Find top issues
  summary.topIssues = Object.entries(issueFrequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([issue, count]) => ({
      issue,
      count,
      percentage: ((count / responses.length) * 100).toFixed(1)
    }));

  // Readiness check
  const invalidResponses = responses.filter(r => r.metadata.data_quality === 'invalid').length;
  const invalidPercentage = (invalidResponses / responses.length) * 100;

  if (invalidPercentage > 50) {
    summary.readinessCheck.isReady = false;
    summary.readinessCheck.blockers.push('Too many invalid responses');
  }

  if (responses.length === 0) {
    summary.readinessCheck.isReady = false;
    summary.readinessCheck.blockers.push('No valid responses found');
  }

  if (questions.length === 0) {
    summary.readinessCheck.isReady = false;
    summary.readinessCheck.blockers.push('No questions detected');
  }

  // Recommendations
  if (summary.warningCount > 0) {
    summary.readinessCheck.recommendations.push('Review validation warnings before import');
  }

  if (invalidPercentage > 20) {
    summary.readinessCheck.recommendations.push('Consider improving data source quality');
  }

  return summary;
}

/**
 * Generate import recommendations
 * @param {Object} surveyData - Survey data
 * @param {Object} dataQualityAnalysis - Data quality analysis
 * @returns {Array} Array of recommendation objects
 */
function generateImportRecommendations(surveyData, dataQualityAnalysis) {
  const recommendations = [];
  const { responses, summary } = surveyData;

  // Data quality recommendations
  if (dataQualityAnalysis && dataQualityAnalysis.overall.score < 80) {
    recommendations.push({
      type: 'quality',
      priority: 'medium',
      title: 'Improve Data Quality',
      description: `Overall data quality score is ${dataQualityAnalysis.overall.score}%. Consider reviewing the source data.`,
      action: 'Review data source and fix formatting issues'
    });
  }

  // Size recommendations
  if (responses.length > 500) {
    recommendations.push({
      type: 'performance',
      priority: 'low',
      title: 'Large Import',
      description: `Importing ${responses.length} responses. This may take several minutes.`,
      action: 'Monitor import progress and avoid navigating away'
    });
  }

  // Question count recommendations
  if (summary.questions_count > 50) {
    recommendations.push({
      type: 'usability',
      priority: 'low',
      title: 'Many Questions',
      description: `${summary.questions_count} questions detected. Consider survey design optimization.`,
      action: 'Review if all questions are necessary for analysis'
    });
  }

  // Completion rate recommendations
  const completionRate = (summary.complete_responses / summary.total_responses) * 100;
  if (completionRate < 60) {
    recommendations.push({
      type: 'quality',
      priority: 'high',
      title: 'Low Completion Rate',
      description: `Only ${completionRate.toFixed(1)}% of responses are complete.`,
      action: 'Investigate data collection process or survey design'
    });
  }

  // Memory usage recommendations
  const memoryEstimate = estimateMemoryUsage(surveyData);
  if (memoryEstimate.airtable_fields_needed > 1) {
    recommendations.push({
      type: 'storage',
      priority: 'medium',
      title: 'Multiple Storage Fields Required',
      description: `Data will require ${memoryEstimate.airtable_fields_needed} Airtable fields due to size.`,
      action: 'Consider splitting survey into smaller imports if possible'
    });
  }

  return recommendations;
}

// Helper functions

/**
 * Get icon name for question type
 * @param {string} type - Question type
 * @returns {string} Icon name for UI
 */
function getQuestionTypeIcon(type) {
  const iconMap = {
    'text': 'Type',
    'long_text': 'FileText',
    'number': 'Hash',
    'rating': 'Star',
    'nps': 'TrendingUp',
    'yes_no': 'ToggleLeft',
    'multiple_choice': 'CircleDot',
    'multiple_select': 'CheckSquare',
    'date': 'Calendar',
    'email': 'Mail'
  };
  return iconMap[type] || 'HelpCircle';
}

/**
 * Get label for question type
 * @param {string} type - Question type
 * @returns {string} Human-readable label
 */
function getQuestionTypeLabel(type) {
  const labelMap = {
    'text': 'Text',
    'long_text': 'Long Text',
    'number': 'Number',
    'rating': 'Rating Scale',
    'nps': 'NPS Score',
    'yes_no': 'Yes/No',
    'multiple_choice': 'Multiple Choice',
    'multiple_select': 'Multiple Select',
    'date': 'Date',
    'email': 'Email'
  };
  return labelMap[type] || 'Unknown';
}

/**
 * Get icon for data quality
 * @param {string} quality - Data quality level
 * @returns {string} Icon name
 */
function getDataQualityIcon(quality) {
  const iconMap = {
    'complete': 'CheckCircle',
    'partial': 'AlertTriangle',
    'invalid': 'XCircle'
  };
  return iconMap[quality] || 'HelpCircle';
}

/**
 * Get color for data quality
 * @param {string} quality - Data quality level
 * @returns {string} CSS color class
 */
function getDataQualityColor(quality) {
  const colorMap = {
    'complete': 'text-green-600',
    'partial': 'text-yellow-600',
    'invalid': 'text-red-600'
  };
  return colorMap[quality] || 'text-gray-600';
}

/**
 * Format answer for preview display
 * @param {any} answer - Answer value
 * @param {string} type - Question type
 * @returns {string} Formatted answer
 */
function formatAnswerForPreview(answer, type) {
  if (answer === null || answer === undefined) {
    return '(empty)';
  }

  switch (type) {
    case 'multiple_select':
      if (Array.isArray(answer)) {
        return answer.length > 2 
          ? `${answer.slice(0, 2).join(', ')} (+${answer.length - 2} more)`
          : answer.join(', ');
      }
      return String(answer);
    
    case 'long_text': {
      const text = String(answer);
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    }
    
    case 'yes_no':
      return answer === true ? 'Yes' : answer === false ? 'No' : String(answer);
    
    case 'rating':
    case 'nps':
      return `${answer}`;
    
    default: {
      const str = String(answer);
      return str.length > 50 ? str.substring(0, 50) + '...' : str;
    }
  }
}

/**
 * Estimate import time based on response count
 * @param {number} responseCount - Number of responses
 * @returns {string} Estimated time
 */
function estimateImportTime(responseCount) {
  // Rough estimate: 10 responses per second with API rate limiting
  const estimatedSeconds = Math.ceil(responseCount / 10);
  
  if (estimatedSeconds < 60) {
    return `~${estimatedSeconds} seconds`;
  } else if (estimatedSeconds < 3600) {
    return `~${Math.ceil(estimatedSeconds / 60)} minutes`;
  } else {
    return `~${Math.ceil(estimatedSeconds / 3600)} hours`;
  }
}

/**
 * Generate data table preview for spreadsheet-like display
 * @param {Object} surveyData - Survey data
 * @param {Object} options - Preview options
 * @returns {Object} Table preview data
 */
export function generateTablePreview(surveyData, options = {}) {
  const { maxRows = 10, maxColumns = 100 } = options;
  const { responses, questionMapping: directMapping } = surveyData;
  
  // Try to get question mapping from direct property first, then from importRecord
  let questionMapping;
  if (directMapping) {
    questionMapping = directMapping;
  } else if (surveyData.importRecord?.['Question Mapping']) {
    questionMapping = JSON.parse(surveyData.importRecord['Question Mapping']);
  } else {
    throw new Error('No question mapping available for table preview generation');
  }
  
  // Create column headers
  const columns = questionMapping.questions.slice(0, maxColumns).map(q => ({
    id: q.id,
    title: q.title,
    type: q.type,
    width: q.type === 'long_text' ? 'wide' : 'normal'
  }));

  // Add quality column
  columns.push({
    id: '_quality',
    title: 'Quality',
    type: 'quality',
    width: 'narrow'
  });

  // Create rows
  const rows = responses.slice(0, maxRows).map((response, index) => {
    const row = {
      id: index,
      originalRow: response.metadata.original_row,
      quality: response.metadata.data_quality,
      cells: {}
    };

    // Add data cells
    columns.forEach(column => {
      if (column.id === '_quality') {
        row.cells[column.id] = {
          value: response.metadata.data_quality,
          formatted: response.metadata.data_quality,
          type: 'quality'
        };
      } else {
        const answer = response.responses[column.id];
        row.cells[column.id] = {
          value: answer ? answer.answer : null,
          formatted: answer ? formatAnswerForPreview(answer.answer, column.type) : '(empty)',
          type: column.type,
          hasWarning: answer && response.metadata.validation_warnings?.some(w => 
            w.includes(column.title.substring(0, 20))
          )
        };
      }
    });

    return row;
  });

  return {
    columns,
    rows,
    hasMoreRows: responses.length > maxRows,
    hasMoreColumns: questionMapping.questions.length > maxColumns,
    totalRows: responses.length,
    totalColumns: questionMapping.questions.length
  };
}